
import React, { useEffect, useState } from "react";
import { keycloak } from "../../lib/keycloak/index.ts";
import { useAppDispatch, useAppSelector } from "../../lib/redux/hook.ts";
import { toBase64 } from "../../lib/utilis/index.ts";
import { colors } from "../../ui/colors.ts";
import { EmailIcon } from "../../ui/icons/emailIcon.tsx";
import Avatar from "../../ui/molecules/avatar/index.tsx";
import { ButtonComponent } from "../../ui/molecules/button/index.tsx";
import { FileInputComponent } from "../../ui/molecules/fileInput/index.tsx";
import { InputComponent } from "../../ui/molecules/input/index.tsx";
import { SelectComponent } from "../../ui/molecules/select/index.tsx";
import { ModalComponent } from "../../ui/organisms/modal/index.tsx";
import { SubscriptionType } from "../financialAdvisor/dto.ts";
import { financialAdvisorActiveSubscription } from "../financialAdvisor/slice.ts";
import { createCompany, resetCreateCompanyRequest, setCreateCompanyCompanyName, setCreateCompanyEmail, setCreateCompanyFinancialAdvisorId, setCreateCompanyFiscalCode, setCreateCompanyName, setCreateCompanyNote, setCreateCompanyPhoneNumber, setCreateCompanySurname, setCreateVat, setOpenNewCompanyModal } from "./slice.ts";

export function NewCompanyModal() {
    const companyState = useAppSelector(state => state.company)
    const financialAdvisorState = useAppSelector(state => state.financialAdvisor)
    const authState = useAppSelector(state => state.auth)
    const layoutState = useAppSelector(state => state.layout)

    const dispatch = useAppDispatch()

    const [file, setFile] = useState<File | null>(null)
    const [avatarToBase64, setAvatarToBase64] = useState<string>('')

    let formData = new FormData();

    useEffect(() => {
        if (companyState.openNewCompanyModal) {
            setFile(null)
            setAvatarToBase64('')
            dispatch(setCreateVat(""))
        }
    }, [companyState.openNewCompanyModal])

    useEffect(() => {
        if (authState.findMeFinancialAdvisorStatus === 'successfully' && companyState.openNewCompanyModal) {
            dispatch(setCreateCompanyFinancialAdvisorId(authState.findMeFinancialAdvisorResponse?.id))
            dispatch(financialAdvisorActiveSubscription(authState.findMeFinancialAdvisorResponse?.id!))
        }
    }, [authState.findMeFinancialAdvisorStatus, companyState.openNewCompanyModal])

    return (
        <ModalComponent
            width="medium"
            open={companyState.openNewCompanyModal}
            handleClose={() => {
                dispatch(setOpenNewCompanyModal(false));
            }}
        >
            <div className={"p-5 bg-white rounded-xl flex flex-col gap-2.5 w-full"}
                style={{
                    boxShadow: '0px 12px 24px 0px rgba(22, 42, 100, 0.12)',
                }}
            >
                <div className="flex items-center justify-between">
                    <span className="text-heading-xs font-semibold text-neutral-600">Aggiungi azienda</span>
                </div>
                {
                    keycloak.hasRealmRole('financial_advisor') &&
                    <div className="flex items-center bg-neutral-200 rounded-3xl flex-row p-3 gap-1">
                        <span className="text-text-sm leading-4 font-semibold text-neutral-800">Puoi aggiungere ancora</span>
                        <span style={{ color: layoutState.theme[500] }} className="text-text-sm leading-4 font-semibold">
                            {financialAdvisorState.financialAdvisorActiveSubscriptionResponse?.maxNumberOfCompanies! - companyState.findAllCompanyResponse?.total! +
                                (financialAdvisorState.financialAdvisorActiveSubscriptionResponse?.maxNumberOfCompanies! - companyState.findAllCompanyResponse?.total! !== 1 ? " aziende" : " azienda")}
                        </span>
                    </div>
                }
                <div className="flex flex-col items-start" style={{ gap: '16px', width: '100%' }}>
                    <div className="flex gap-[16px] flex-row w-full">
                        <Avatar
                            type="single"
                            size="md"
                            shape="circle"
                            imageUrl={avatarToBase64}
                            altTextInitials={(companyState.createCompanyRequest.name[0] || 'A') + (companyState.createCompanyRequest.surname[0] || 'A')}
                        />
                        <FileInputComponent
                            id={"create-financial-advisor-avatar"}
                            onChange={(e) => {
                                if (e.length) {
                                    setFile(e[0])
                                    toBase64(e[0]).then(data => setAvatarToBase64('data:' + e[0].type + ';base64,' + data))
                                }
                            }}
                            color={"gray"}
                            iconSize="32"
                            iconBackground={colors.neutral[100]}
                            infoText={false}
                        />
                    </div>
                    <div className=" flex flex-row gap-4 w-full">
                        <InputComponent
                            label="Ragione sociale"
                            type={"text"}
                            placeholder={"Ragione sociale"}
                            defaultValue={companyState.createCompanyRequest.companyName}
                            onChangeText={(value) => dispatch(setCreateCompanyCompanyName(value))}
                            required={companyState.createCompanyRequest.name.trim().length === 0 && companyState.createCompanyRequest.surname.trim().length === 0}
                        />
                    </div>
                    <div className=" flex flex-row gap-4 w-full">
                        <InputComponent
                            label="Codice Fiscale"
                            type={"text"}
                            required
                            defaultValue={companyState.createCompanyRequest.fiscalCode.trim()}
                            placeholder={"Codice Fiscale"}
                            onChangeText={(value) => dispatch(setCreateCompanyFiscalCode(value))}
                        />
                        <InputComponent
                            label="Partita IVA"
                            type={"text"}
                            startIcon={
                                <div className=" flex flex-row items-center">
                                    <span className=" text-text-sm font-medium text-neutral-400">IT</span>
                                </div>
                            }
                            placeholder={"Partita IVA"}
                            defaultValue={companyState.createCompanyRequest.vat}
                            onChangeText={(value) => dispatch(setCreateVat(value))}
                            required
                        />
                    </div>
                    <div className=" flex flex-row gap-4 w-full">
                        <InputComponent
                            label="Nome"
                            type={"text"}
                            defaultValue={companyState.createCompanyRequest.name}
                            placeholder={"Nome"}
                            onChangeText={(value) => dispatch(setCreateCompanyName(value))}
                            required={companyState.createCompanyRequest.companyName.length === 0 || companyState.createCompanyRequest.surname.length !== 0}
                        />
                        <InputComponent
                            label="Cognome"
                            type={"text"}
                            defaultValue={companyState.createCompanyRequest.surname}
                            placeholder={"Cognome"}
                            onChangeText={(value) => dispatch(setCreateCompanySurname(value))}
                            required={companyState.createCompanyRequest.companyName.length === 0 || companyState.createCompanyRequest.name.length !== 0}
                        />
                    </div>
                    <div className=" flex flex-row gap-4 w-full">
                        <InputComponent
                            type={"text"}
                            label="Email"
                            defaultValue={companyState.createCompanyRequest.email}
                            value={companyState.createCompanyRequest.email.toLowerCase()}
                            placeholder="Email"
                            startIcon={<EmailIcon size={"20"} color={colors.neutral[400]} />}
                            onChangeText={(value) => dispatch(setCreateCompanyEmail(value.toLowerCase()))}
                            required
                        />
                        <InputComponent
                            required
                            label="Telefono"
                            startIcon={
                                <div className=" flex flex-row items-center">
                                    <span className=" text-text-sm font-medium text-neutral-400">+39</span>
                                </div>
                            }
                            type={"text"}
                            defaultValue={companyState.createCompanyRequest.phoneNumber}
                            placeholder={"Telefono"}
                            onChangeText={(value) => dispatch(setCreateCompanyPhoneNumber(value))}
                        />
                    </div>
                    <div className=" items-center flex flex-row gap-4 w-full">
                        {
                            !keycloak.hasRealmRole("financial_advisor") &&
                            <SelectComponent
                                label="Commercialista"
                                required
                                placeholder={"Tutti i commercialisti"}
                                key={'new-company-by-financial-advisor'}
                                onChange={(value) => dispatch(setCreateCompanyFinancialAdvisorId(value))}
                                options={financialAdvisorState.findAllFinancialAdvisorResponse?.data.filter(sub => sub.subscriptionType !== SubscriptionType.expired && sub.companies.length < sub.maxNumberOfCompanies).map((value) => ({ label: (value.name.trim() ? (value.name + ' ' + value.surname) : value.commercialName), value: value.id }))!}
                                value={companyState.createCompanyRequest.financialAdvisorId.toString()}
                            />
                        }
                    </div>
                    <InputComponent
                        type={"text"}
                        label="Descrizione"
                        placeholder="Un po' di informazioni su di te"
                        supportingText={(275 - companyState.createCompanyRequest.note.length).toString() + " caratteri rimasti"}
                        onChangeText={(value) => dispatch(setCreateCompanyNote(value))}
                        defaultValue={companyState.createCompanyRequest.note}
                    />
                </div>
                <div className={"flex gap-3 items-center"}>
                    <ButtonComponent
                        label="Annulla"
                        size={"sm"}
                        iconPosition={"off"}
                        variant={"outline"}
                        color={"gray"}
                        onClick={() => {
                            dispatch(setOpenNewCompanyModal(false))
                            dispatch(resetCreateCompanyRequest())
                        }}
                        fullWidth
                    />
                    <ButtonComponent
                        label={"Aggiungi"}
                        onClick={() => {
                            if (file !== null)
                                formData.append('avatar', file)
                            formData.append('companyCreationDTO', JSON.stringify(companyState.createCompanyRequest))
                            dispatch(createCompany(formData));
                            dispatch(setOpenNewCompanyModal(false));
                        }}
                        color={"blue"}
                        variant={"solid"}
                        size={"sm"}
                        iconPosition={"off"}
                        fullWidth
                        disabled={
                            companyState.createCompanyRequest.email === '' ||
                            !/^[A-z0-9\.\+_-]+@[A-z0-9\._-]+\.[A-z]{2,6}$/.
                                test(companyState.createCompanyRequest.email) ||
                            (companyState.createCompanyRequest.vat.length < 8 ||
                                companyState.createCompanyRequest.vat.length > 16) ||
                            !/^\d+$/.test(companyState.createCompanyRequest.vat) ||
                            companyState.createCompanyRequest.financialAdvisorId === 0 ||
                            (companyState.createCompanyRequest.fiscalCode.trim().length > 16 || 
                            companyState.createCompanyRequest.fiscalCode.length < companyState.createCompanyRequest.vat.length) ||
                            !/^\d{9,10}$/.test(companyState.createCompanyRequest.phoneNumber)
                            || (!companyState.createCompanyRequest.companyName.trim() && !(companyState.createCompanyRequest.name.trim() && companyState.createCompanyRequest.surname.trim()))
                        }
                    />
                </div>
            </div>
        </ModalComponent>
    )
}